import { useEffect, useMemo, useState } from "react";
import { FoldCard } from "./FoldCard";
import { fetchAllocation, loadInFcrAllocation, type AllocationFile } from "../lib/allocationApi";

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page disabled:cursor-not-allowed disabled:opacity-40";
const btnOn = "rounded-md border border-accent bg-accent px-2 py-1 text-white";

const ADMIN_ONLY_TITLE = "Admin only";

export function AllocationAdminCard({ canControl = true }: { canControl?: boolean }) {
  const [file, setFile] = useState<AllocationFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [block, setBlock] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [copyHint, setCopyHint] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [open, setOpen] = useState(false);
  const [loadingInFcr, setLoadingInFcr] = useState(false);
  const [loadHint, setLoadHint] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllocation()
      .then((data) => {
        if (cancelled) return;
        setFile(data);
        setBlock(data.active_block || data.current_block);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (!file || !block) return null;
    return file.blocks.find((item) => item.name === block) ?? null;
  }, [file, block]);

  const visibleIds = useMemo(() => {
    const ids = selected?.ids ?? [];
    const needle = query.trim().toLowerCase();
    if (!needle) return ids;
    return ids.filter((id) => id.toLowerCase().includes(needle));
  }, [selected, query]);

  async function loadInFcr() {
    if (loadingInFcr) return;
    const ok = window.confirm(
      "Load in_fcr charger IDs into every clock block and PQa–PQg? load_balance is left unchanged.",
    );
    if (!ok) return;
    setLoadingInFcr(true);
    setLoadHint(null);
    setError(null);
    try {
      const data = await loadInFcrAllocation();
      setFile(data);
      setBlock(data.active_block || data.current_block);
      const count = data.load?.count ?? data.blocks[0]?.count ?? 0;
      setLoadHint(`Loaded ${count} in_fcr IDs into every block`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setOpen(true);
    } finally {
      setLoadingInFcr(false);
    }
  }

  async function copyPath() {
    if (!file?.path) return;
    try {
      await navigator.clipboard.writeText(file.path);
      setCopyHint("Copied");
    } catch {
      setCopyHint("Copy failed");
    }
    window.setTimeout(() => setCopyHint(null), 2000);
  }

  const current = file?.blocks.find((item) => item.name === (file.active_block || file.current_block));

  return (
    <FoldCard
      title="Allocated chargers"
      open={open}
      onToggle={() => setOpen((value) => !value)}
      summary={
        <>
          Step 2. The file on {file?.host || "m4-1"}
          {file?.current_block ? ` · clock block ${file.current_block}` : ""}
          {current ? ` · ${current.count}` : ""}
          {file?.active_block ? ` · active ${file.active_block}` : ""}
          {loadHint ? ` · ${loadHint}` : ""}
        </>
      }
      actions={
        <>
          <button
            type="button"
            className={btn}
            style={{ fontSize: "var(--font-micro)" }}
            disabled={!canControl || loadingInFcr}
            title={!canControl ? ADMIN_ONLY_TITLE : undefined}
            onClick={() => void loadInFcr()}
          >
            {loadingInFcr ? "Loading in_fcr IDs…" : "Load in_fcr IDs into every block"}
          </button>
          <button type="button" className={btn} style={{ fontSize: "var(--font-micro)" }} disabled={!file?.path} onClick={() => void copyPath()}>
            {copyHint || "Copy path"}
          </button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <code className="break-all text-text-primary" style={{ fontSize: "var(--font-micro)" }}>
          {file?.path || "—"}
        </code>
      </div>
      {file?.mtime && (
        <p className="mt-1 text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
          {file.bytes != null ? `${file.bytes.toLocaleString()} bytes` : ""}
          {file.mtime ? ` · ${file.mtime}` : ""}
          {file.load_balance ? ` · load_balance ${file.load_balance.length}` : ""}
        </p>
      )}
      {(error || file?.error) && (
        <p className="mt-2 text-status-error" style={{ fontSize: "var(--font-body)" }}>
          {error || file?.error}
        </p>
      )}

      {file && file.blocks.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1" style={{ fontSize: "var(--font-micro)" }}>
          {file.blocks.map((item) => {
            const on = item.name === block;
            const current = item.name === (file.active_block || file.current_block);
            return (
              <button
                key={item.name}
                type="button"
                className={on ? btnOn : btn}
                onClick={() => setBlock(item.name)}
              >
                {item.name}
                {current ? " ·" : ""} {item.count}
              </button>
            );
          })}
        </div>
      )}

      <label className="mt-3 block text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
        Filter
        <input
          className="ml-2 rounded-md border border-border-subtle bg-page px-2 py-1 text-text-primary"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="charger id"
        />
        <span className="ml-2">
          {visibleIds.length}
          {selected ? ` / ${selected.count}` : ""}
        </span>
      </label>
      <pre
        className="mt-2 max-h-64 overflow-auto rounded-md border border-border-subtle bg-page p-2 text-text-primary"
        style={{ fontSize: "var(--font-nano)" }}
      >
        {visibleIds.length ? visibleIds.join("\n") : "—"}
      </pre>

      <div className="mt-3">
        <button
          type="button"
          className={btn}
          style={{ fontSize: "var(--font-micro)" }}
          onClick={() => setShowPreview((open) => !open)}
        >
          {showPreview ? "Hide file preview" : "File preview"}
        </button>
        {showPreview && file?.preview && (
          <pre
            className="mt-2 max-h-64 overflow-auto rounded-md border border-border-subtle bg-page p-2 text-text-secondary"
            style={{ fontSize: "var(--font-nano)" }}
          >
            {file.preview}
          </pre>
        )}
        {showPreview && file?.truncated && (
          <p className="mt-1 text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
            First lines of {file.lines.toLocaleString()}. The lists above are the charger IDs.
          </p>
        )}
      </div>
    </FoldCard>
  );
}
